import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronDown, Plus, Trash2, Pencil, Check, Smartphone } from "lucide-react";
import {
  DEVICE_CATEGORIES,
  DEVICE_CATALOG,
  PLATFORMS_BY_CATEGORY,
  RELEASE_VERSIONS,
  OTHERS_LABEL,
} from "@/lib/deviceCatalog";

/**
 * Single-select dropdown that stays open until the user explicitly clicks "Done".
 * Uses a Popover with a scrollable list of radio-style options and a bottom "Done" button.
 */
function SelectWithDone({ value, options, onChange, placeholder, disabled, testid }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || "");

  useEffect(() => { setDraft(value || ""); }, [value, open]);

  const commit = () => { onChange(draft || ""); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={(o) => { if (!disabled) setOpen(o); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className={`w-full flex items-center justify-between rounded-md border px-3 py-2 text-sm text-left transition-colors ${
            disabled
              ? "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"
              : "bg-white border-slate-300 text-slate-900 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          }`}
        >
          <span className={value ? "" : "text-slate-400"}>{value || placeholder}</span>
          <ChevronDown className="w-4 h-4 text-slate-400 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" data-testid={`${testid}-content`}>
        <ScrollArea className="max-h-60">
          <div className="p-1">
            {options.length === 0 && (
              <div className="text-xs text-slate-400 px-3 py-2">No options available</div>
            )}
            {options.map((opt) => {
              const active = draft === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setDraft(opt)}
                  data-testid={`${testid}-opt-${opt.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-left transition-colors ${
                    active ? "bg-slate-900 text-white" : "hover:bg-slate-100 text-slate-700"
                  }`}
                >
                  <span
                    className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center shrink-0 ${
                      active ? "border-white bg-white" : "border-slate-400"
                    }`}
                  >
                    {active && <span className="w-1.5 h-1.5 rounded-full bg-slate-900" />}
                  </span>
                  {opt}
                </button>
              );
            })}
          </div>
        </ScrollArea>
        <div className="flex items-center justify-between border-t border-slate-200 p-2 gap-2">
          <button
            type="button"
            onClick={() => { setDraft(""); }}
            className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1"
            data-testid={`${testid}-clear`}
          >
            Clear
          </button>
          <Button
            type="button"
            size="sm"
            onClick={commit}
            disabled={!draft}
            className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50"
            data-testid={`${testid}-done`}
          >
            <Check className="w-3.5 h-3.5 mr-1" /> Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const EMPTY_DRAFT = {
  category: "",
  manufacturer: "",
  model: "",
  platform: "",
  platform_custom: "",
  release_version: "",
  release_version_custom: "",
};

function DeviceForm({ draft, setDraft }) {
  const setField = (k, v) => setDraft((prev) => ({ ...prev, [k]: v }));

  const manufacturers = useMemo(() => {
    if (!draft.category) return [];
    return Object.keys(DEVICE_CATALOG[draft.category] || {});
  }, [draft.category]);

  const models = useMemo(() => {
    if (!draft.category || !draft.manufacturer) return [];
    return DEVICE_CATALOG[draft.category]?.[draft.manufacturer] || [];
  }, [draft.category, draft.manufacturer]);

  const platforms = useMemo(() => {
    if (!draft.category) return [];
    return PLATFORMS_BY_CATEGORY[draft.category] || [];
  }, [draft.category]);

  const releases = useMemo(() => {
    if (!draft.platform || draft.platform === OTHERS_LABEL) return [];
    return RELEASE_VERSIONS[draft.platform] || [];
  }, [draft.platform]);

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-slate-800 text-sm">Category <span className="text-red-500">*</span></Label>
        <div className="mt-1.5">
          <SelectWithDone
            value={draft.category}
            options={DEVICE_CATEGORIES}
            onChange={(v) => setDraft({ ...EMPTY_DRAFT, category: v })}
            placeholder="Select category"
            testid="select-category"
          />
        </div>
      </div>

      <div>
        <Label className="text-slate-800 text-sm">Manufacturer <span className="text-red-500">*</span></Label>
        <div className="mt-1.5">
          <SelectWithDone
            value={draft.manufacturer}
            options={manufacturers}
            onChange={(v) => setDraft((prev) => ({ ...prev, manufacturer: v, model: "" }))}
            placeholder={draft.category ? "Select manufacturer" : "Select category first"}
            disabled={!draft.category}
            testid="select-manufacturer"
          />
        </div>
      </div>

      <div>
        <Label className="text-slate-800 text-sm">Model <span className="text-red-500">*</span></Label>
        <div className="mt-1.5">
          {draft.manufacturer === OTHERS_LABEL ? (
            <Input
              value={draft.model}
              onChange={(e) => setField("model", e.target.value)}
              placeholder="Enter model name"
              data-testid="input-model-custom"
            />
          ) : (
            <SelectWithDone
              value={draft.model}
              options={models}
              onChange={(v) => setField("model", v)}
              placeholder={draft.manufacturer ? "Select model" : "Select manufacturer first"}
              disabled={!draft.manufacturer}
              testid="select-model"
            />
          )}
        </div>
      </div>

      <div>
        <Label className="text-slate-800 text-sm">Platform <span className="text-red-500">*</span></Label>
        <div className="mt-1.5">
          <SelectWithDone
            value={draft.platform}
            options={platforms}
            onChange={(v) => setDraft((prev) => ({ ...prev, platform: v, release_version: "", release_version_custom: "" }))}
            placeholder={draft.category ? "Select platform" : "Select category first"}
            disabled={!draft.category}
            testid="select-platform"
          />
        </div>
        {draft.platform === OTHERS_LABEL && (
          <Input
            value={draft.platform_custom}
            onChange={(e) => setField("platform_custom", e.target.value)}
            placeholder="Enter platform name"
            className="mt-2"
            data-testid="input-platform-custom"
          />
        )}
      </div>

      <div>
        <Label className="text-slate-800 text-sm">Release Version <span className="text-red-500">*</span></Label>
        <div className="mt-1.5">
          {draft.platform === OTHERS_LABEL ? (
            <Input
              value={draft.release_version}
              onChange={(e) => setField("release_version", e.target.value)}
              placeholder="Enter release version"
              data-testid="input-release-custom-direct"
            />
          ) : (
            <SelectWithDone
              value={draft.release_version}
              options={[...releases, OTHERS_LABEL]}
              onChange={(v) => setDraft((prev) => ({ ...prev, release_version: v, release_version_custom: "" }))}
              placeholder={draft.platform ? "Select release version" : "Select platform first"}
              disabled={!draft.platform}
              testid="select-release"
            />
          )}
        </div>
        {draft.release_version === OTHERS_LABEL && draft.platform !== OTHERS_LABEL && (
          <Input
            value={draft.release_version_custom}
            onChange={(e) => setField("release_version_custom", e.target.value)}
            placeholder="Enter release version"
            className="mt-2"
            data-testid="input-release-custom"
          />
        )}
      </div>
    </div>
  );
}

function isDraftComplete(d) {
  if (!d.category || !d.manufacturer || !d.model) return false;
  if (!d.platform) return false;
  if (d.platform === OTHERS_LABEL && !d.platform_custom.trim()) return false;
  if (!d.release_version) return false;
  if (d.release_version === OTHERS_LABEL && !d.release_version_custom.trim()) return false;
  return true;
}

function formatDeviceLabel(d) {
  const plat = d.platform === OTHERS_LABEL ? d.platform_custom : d.platform;
  const ver = d.release_version === OTHERS_LABEL ? d.release_version_custom : d.release_version;
  return `${d.model} — ${plat} ${ver}`.trim();
}

/**
 * DeviceInventory
 * - Value: array of device objects
 * - onChange: (nextArray) => void
 */
export default function DeviceInventory({ value = [], onChange }) {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [pending, setPending] = useState([]); // devices staged inside the drawer
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [editingIdx, setEditingIdx] = useState(null);

  const startDrawer = () => {
    setPending(value.map((d) => ({ ...EMPTY_DRAFT, ...d })));
    setDraft(EMPTY_DRAFT);
    setEditingIdx(null);
    setOpenDrawer(true);
  };

  const canSaveCurrent = isDraftComplete(draft);

  const stageCurrent = () => {
    if (!canSaveCurrent) return;
    const clean = {
      category: draft.category,
      manufacturer: draft.manufacturer,
      model: draft.model,
      platform: draft.platform,
      platform_custom: draft.platform === OTHERS_LABEL ? draft.platform_custom : "",
      release_version: draft.release_version,
      release_version_custom: draft.release_version === OTHERS_LABEL ? draft.release_version_custom : "",
    };
    if (editingIdx !== null) {
      setPending((prev) => prev.map((d, i) => (i === editingIdx ? clean : d)));
    } else {
      setPending((prev) => [...prev, clean]);
    }
    setDraft(EMPTY_DRAFT);
    setEditingIdx(null);
  };

  const editStaged = (idx) => {
    setDraft({ ...EMPTY_DRAFT, ...pending[idx] });
    setEditingIdx(idx);
  };
  const removeStaged = (idx) => {
    setPending((prev) => prev.filter((_, i) => i !== idx));
    if (editingIdx === idx) { setDraft(EMPTY_DRAFT); setEditingIdx(null); }
  };

  const apply = () => {
    // If the user filled a draft but didn't click "Add Another Device", stage it now.
    let finalList = pending;
    if (isDraftComplete(draft)) {
      const clean = {
        category: draft.category,
        manufacturer: draft.manufacturer,
        model: draft.model,
        platform: draft.platform,
        platform_custom: draft.platform === OTHERS_LABEL ? draft.platform_custom : "",
        release_version: draft.release_version,
        release_version_custom: draft.release_version === OTHERS_LABEL ? draft.release_version_custom : "",
      };
      finalList = editingIdx !== null
        ? pending.map((d, i) => (i === editingIdx ? clean : d))
        : [...pending, clean];
    }
    onChange(finalList);
    setOpenDrawer(false);
  };

  return (
    <div>
      {/* Devices list on Step 4 */}
      {value.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-md p-8 text-center bg-slate-50/50" data-testid="devices-empty-state">
          <Smartphone className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <div className="text-sm font-medium text-slate-600">No devices added yet</div>
          <div className="text-xs text-slate-500 mt-1">Click <b>+ Add Device</b> below to add the first one.</div>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-md divide-y divide-slate-200 bg-white" data-testid="devices-list">
          {value.map((d, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-3 gap-3" data-testid={`device-row-${i}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{formatDeviceLabel(d)}</div>
                <div className="text-xs text-slate-500 truncate">
                  {d.category} · {d.manufacturer}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setPending(value.map((x) => ({ ...EMPTY_DRAFT, ...x })));
                    setDraft({ ...EMPTY_DRAFT, ...d });
                    setEditingIdx(i);
                    setOpenDrawer(true);
                  }}
                  className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
                  data-testid={`device-edit-${i}`}
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => onChange(value.filter((_, idx) => idx !== i))}
                  className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                  data-testid={`device-remove-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3">
        <Button
          type="button"
          onClick={startDrawer}
          variant="outline"
          className="border-slate-300 text-slate-900 hover:bg-slate-50"
          data-testid="btn-open-add-device"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add Device
        </Button>
      </div>

      {/* Right-side drawer */}
      <Sheet open={openDrawer} onOpenChange={setOpenDrawer}>
        <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0 gap-0" data-testid="drawer-add-device">
          <SheetHeader className="p-6 border-b border-slate-200">
            <SheetTitle>Add Device</SheetTitle>
            <SheetDescription>
              Fill in device details. You can add multiple devices in one go.
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="flex-1">
            <div className="p-6 space-y-6">
              {/* Chips of staged devices in this drawer session */}
              {pending.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    Devices in this session ({pending.length})
                  </div>
                  <div className="space-y-2" data-testid="drawer-staged-list">
                    {pending.map((d, i) => (
                      <div
                        key={i}
                        className={`flex items-center justify-between border rounded-md px-3 py-2 gap-2 ${
                          editingIdx === i ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white"
                        }`}
                        data-testid={`drawer-chip-${i}`}
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{formatDeviceLabel(d)}</div>
                          <div className="text-xs text-slate-500 truncate">{d.category} · {d.manufacturer}</div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => editStaged(i)}
                            className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
                            data-testid={`drawer-chip-edit-${i}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeStaged(i)}
                            className="p-1 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                            data-testid={`drawer-chip-remove-${i}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">
                  {editingIdx !== null ? "Editing device" : "New device"}
                </div>
                <DeviceForm draft={draft} setDraft={setDraft} />
              </div>

              <div className="pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={stageCurrent}
                  disabled={!canSaveCurrent}
                  className="w-full border-dashed border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  data-testid="btn-add-another-device"
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  {editingIdx !== null ? "Save changes & add another" : "Add Another Device"}
                </Button>
                {!canSaveCurrent && (draft.category || draft.manufacturer) && (
                  <div className="text-[11px] text-slate-400 mt-1.5 text-center">
                    Fill Category, Manufacturer, Model, Platform, and Release Version to add.
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>

          <div className="border-t border-slate-200 p-4 flex items-center justify-between gap-3 bg-white">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpenDrawer(false)}
              data-testid="btn-drawer-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={apply}
              disabled={pending.length === 0 && !isDraftComplete(draft)}
              className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50"
              data-testid="btn-drawer-apply"
            >
              Apply
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
